import { Component, OnDestroy, Input } from '@angular/core';
import { Validators, FormGroup, FormBuilder, FormControl } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatRadioChange } from '@angular/material/radio';
import { MatSelectChange } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ReCaptchaV3Service } from 'ng-recaptcha-2';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { ProductAttribute } from 'src/@omnial/_models/catalog/product-attribute.model';
import { ProductDetail, ProductOption, SelectAttribute } from 'src/@omnial/_models/catalog/product.model';
import { RegRequest, RegResult, LogonRequest, LogonResult, RefreshResponse } from 'src/@omnial/_models/customer/account.model';
import { Customer } from 'src/@omnial/_models/customer/customer.model';
import { AccountService } from 'src/@omnial/_services/customer/account.service';
import { ClaimsService } from 'src/@omnial/_services/customer/claims.service';
import { CustomerService } from 'src/@omnial/_services/customer/customer.service';
import { CartService } from 'src/@omnial/_services/order/cart.service';
import { CurrencyService } from 'src/@omnial/_services/price/currency.service';
import { AppSettings } from 'src/app/app.settings';
import { EmailRegEx, MatchValidator } from 'src/@omnial/_utils/app-validators';

@Component({
    selector: 'app-controls-detail',
    templateUrl: './../../../app/product-controls/controls-detail/controls-detail.component.html',
    styleUrls: ['./../../../app/product-controls/controls-detail/controls-detail.component.scss'],
    standalone: false
})
export class ControlsDetailComponent implements OnDestroy {
  @Input() product: ProductDetail;
  @Input() listId: string;
  public customer: Customer;
  public productAvailable = true;
  public hasAttributes = false;
  public loggedIn = false;
  public stockRegister = false;
  public hidePassword = false;
  public resetHeader = 'Need a new Password?';
  public recentToken = '';

  public attributeForms: FormGroup[] = [];

  public cartForm = new FormGroup({
    quantity: new FormControl(1, Validators.compose([Validators.required]))
  });

  public loginForm = new FormGroup({
    email: new FormControl('', Validators.compose([Validators.required, Validators.email, EmailRegEx])),
    password: new FormControl('', Validators.compose([Validators.required, Validators.minLength(6)])),
  });

  public registerForm = new FormGroup({
    firstName: new FormControl('', Validators.compose([Validators.required])),
    lastName: new FormControl('', Validators.compose([Validators.required])),
    email: new FormControl('', Validators.compose([Validators.required, Validators.email, EmailRegEx])),
    newsLetter: new FormControl(true),
    password: new FormControl('', Validators.compose([Validators.required, Validators.minLength(6)])),
    confirmPassword: new FormControl('', Validators.compose([Validators.required, Validators.minLength(6)]))
  }, {
    validators: MatchValidator
  });

  informed = false;
  public subscriptions: Subscription[] = [];
  public multiples: string[] = [];

  constructor(
    public settings: AppSettings,
    public customerService: CustomerService,
    public cartService: CartService,
    public formBuilder: FormBuilder,
    public recaptchaV3Service: ReCaptchaV3Service,
    public accountService: AccountService,
    public claimsService: ClaimsService,
    public snackBar: MatSnackBar,
    public toastr: ToastrService,
    public currencyService: CurrencyService) {
      this.subscriptions.push(this.customerService.customer.subscribe(
        res => {
          this.customer = res as Customer;
          if (this.customer) {
            this.isLoggedIn();
          }
        }));
    }

  ngOnDestroy(): void {
    if (this.subscriptions && this.subscriptions.length > 0) {
      this.subscriptions.forEach((sub) => { sub.unsubscribe(); });
    }
  }

  public init(product?: ProductDetail): void {
    if (product) {
      this.product = product;
    }
    if (this.attributeForms?.length > 0) { // Timing issue to prevent duplicate display or attributes
      return;
    }
    if (this.product?.stockQuantity > 0) {
      this.productAvailable = true;
    } else {
      this.productAvailable = false;
    }
    if (this.product?.attributes && this.product?.attributes.length > 0) {
      this.hasAttributes = true;
      this.product.attributes.forEach((attribute) => {
        // We need to check to see if this is a "Multi" attribute
        // We can tell when the 'textPrompt', 'controlType' and Options are all equal
        const multiple = attribute.controlType === 'RadioList' && this.product.attributes.some(a => a.productAttributeId !== attribute.productAttributeId && a.textPrompt === attribute.textPrompt && a.controlType === attribute.controlType && this.optionsEqual(a.options, attribute.options))
        if (multiple) {
          if (!this.multiples.some(m => m === attribute.textPrompt)) {
            this.addMultiAttribute(this.product.attributes.filter(a => a.textPrompt === attribute.textPrompt && a.controlType === attribute.controlType && this.optionsEqual(a.options, attribute.options)));
            this.multiples.push(attribute.textPrompt);
          }
        } else {
          this.addStandardAttribute(attribute);
        }
      });
    } else {
      this.hasAttributes = false;
    }
  }

  public tierFlex(): number {
    switch (this.product?.tierPrices?.length) {
      case 7:
      case 6:
      case 5:
        return 20;
      case 4:
        return 25;
      case 3:
        return 33;
      case 2:
        return 50;
      default:
        return 25;
    }
  }

  private optionsEqual(optionsOne: ProductOption[], optionsTwo: ProductOption[]): boolean {
    if (optionsOne.length !== optionsTwo.length) {
      return false;
    }
    optionsOne.forEach((option) => {
      if (!this.optionEqual(option, optionsTwo)) {
        return false;
      }
    });
    optionsTwo.forEach((option) => {
      if (!this.optionEqual(option, optionsOne)) {
        return false;
      }
    });
    return true;
  }

  private optionEqual(option: ProductOption, options: ProductOption[]): boolean {
    if (option.valueType !== 'AssociatedToProduct') {
      return false;
    }
    return options.some(o => o.attributeValueTypeId === option.attributeValueTypeId && o.associatedProductId === option.associatedProductId && o.quantity === option.quantity)
  }

  public addMultiAttribute(attributes: ProductAttribute[]) {
    // Get the options
    let options: ProductOption[] = [];
    attributes.forEach((attribute) => {
      options = options.concat(attribute.options);
    })
    var distinct: ProductOption[] = [];
    options.forEach((option) => {
      if (!distinct.some(o => o.attributeValueTypeId === option.attributeValueTypeId && o.associatedProductId === option.associatedProductId && o.quantity === option.quantity)) {
        distinct.push(option);
      }
    });
    const attributeForm = this.formBuilder.group({
      multi: true,
      textPrompt: attributes[0].textPrompt,
      productAttributeId: attributes[0].productAttributeId, // not really needed
      maximum: attributes.length,
      noSelected: 0,
      controlType: 'Multi',
      options: null,
      productAttributeIds: null
    });
    let productAttributeIds: number[] = [];
    attributes.forEach((attribute) => { productAttributeIds.push(attribute.productAttributeId); })
    attributeForm.patchValue({ productAttributeIds });

    distinct.forEach((option) => {
      const selectAttribute = new SelectAttribute();
      selectAttribute.attributeId = attributes[0].id;
      selectAttribute.optionId = option.id;
      selectAttribute.attributeId = attributes[0].id;
      selectAttribute.name = option.name;
      selectAttribute.productId = option.associatedProductId;
      selectAttribute.noSelected = 0;
      let options = attributeForm.controls.options.value as SelectAttribute[];
      if (options) {
        options.push(selectAttribute);
      } else {
        options = [selectAttribute];
      }
      attributeForm.patchValue({ options });
    });
    if (this.attributeForms) {
      this.attributeForms.push(attributeForm);
    } else {
      this.attributeForms = [attributeForm];
    }
  }

  public addStandardAttribute(attribute: ProductAttribute) {
    if (attribute.options && attribute.options.length > 0) {
      const attributeForm = this.formBuilder.group({
        multi: false,
        textPrompt: attribute.textPrompt,
        productAttributeId: attribute.productAttributeId,
        controlType: attribute.controlType,
        options: null,
        selectedOptions: null,
        selectedOption: null
      });
      if (attributeForm.controls.controlType.value === 'Checkboxes' || attributeForm.controls.controlType.value === 'ReadonlyCheckboxes') {
        attributeForm.addControl('selectedOptions', new FormControl({ value: null, disabled: attributeForm.controls.controlType.value === 'ReadonlyCheckboxes' }));
      } else {
        attributeForm.addControl('selectedOption', new FormControl(null, Validators.required)); // TODO - Are they required? e.g. Text box
      }
      attribute.options.forEach((option) => {
        const selectAttribute = new SelectAttribute();
        selectAttribute.attributeId = attribute.id;
        selectAttribute.optionId = option.id;
        selectAttribute.attributeId = attribute.id;
        selectAttribute.selected = option.isPreSelected || attribute.controlType === 'ReadonlyCheckboxes'; // Assume RO Checkboxes are mandatory?
        selectAttribute.name = option.name;
        selectAttribute.productId = option.associatedProductId;
        if (this.product?.attributes?.length === 1 && option.isPreSelected && option.associatedProductId) {
          // this.parent.swapProduct(option.associatedProductId); TODO - Use emitter
        }
        let options = attributeForm.controls.options.value as SelectAttribute[];
        if (options) {
          options.push(selectAttribute);
        } else {
          options = [selectAttribute];
        }
        attributeForm.patchValue({ options });
        if (selectAttribute.selected) {
          if (attribute.controlType === 'Checkboxes' || attribute.controlType === 'ReadonlyCheckboxes') {
            let selectedOptions = attributeForm.controls.selectedOptions.value as SelectAttribute[];
            if (selectedOptions) {
              selectedOptions.push(selectAttribute);
            } else {
              selectedOptions = [selectAttribute];
            }
            attributeForm.patchValue({ selectedOptions });
          } else {
            attributeForm.patchValue({ selectedOption: selectAttribute });
          }
        }
      });
      if (this.attributeForms) {
        this.attributeForms.push(attributeForm);
      } else {
        this.attributeForms = [attributeForm];
      }
    }
  }

  public addToWishList(): void {
    if (this.attributeForms && this.attributeForms.length > 0) {
      let attributesValid = true;
      this.attributeForms.forEach((attributeForm) => {
        const attribute = this.product.attributes.find(a => a.productAttributeId === attributeForm.controls.productAttributeId.value);
        attribute.options.forEach((option) => {
          option.selected = false;
        });
        attributesValid = attributesValid && attributeForm.valid;
        if (attributeForm.controls.selectedOptions?.value) {
          attributeForm.controls.selectedOptions.value.forEach((selectedValue: SelectAttribute) => {
            attribute.options.find(o => o.id === selectedValue.optionId).selected = true;
          });
        } else if (attributeForm.controls.selectedOption?.value) {
          const selectedValue = attributeForm.controls.selectedOption.value as SelectAttribute;
          attribute.options.find(o => o.id === selectedValue.optionId).selected = true;
        }
      });
      if (attributesValid) {
        this.product.listName = "Product Detail";
        this.product.listId = this.listId;
        this.cartService.addToWishListWithAttributes(this.product);
      } else {
        const message = 'please ensure you have chosen the options correctly';
        this.snackBar.open(message, 'X', { panelClass: ['error'], verticalPosition: 'top', duration: 3000 });
      }
    } else {
      this.product.listName = "Product Detail";
      this.product.listId = this.listId;
      this.cartService.addToWishListDetail(this.product);
    }
  }

  public addToCart(quantity?: number): void {
    if (quantity) {
      this.cartForm.patchValue({ quantity });
    }
    if (this.attributeForms && this.attributeForms.length > 0) {
      let attributesValid = true;
      this.attributeForms.forEach((attributeForm) => {
        if (attributeForm.controls.multi.value) {
          // We may have pre-selected values ... get rid
          // First have we chosen the correct amount
          if (attributeForm.controls.maximum.value !== attributeForm.controls.noSelected.value) {
            attributesValid = false;
          } else {
            const productAttributeIds: number[] = attributeForm.controls.productAttributeIds.value;
            // Set all selections to false (maybe there are some pre-selected ... sanity check)
            productAttributeIds.forEach((productAttributeId) => {
              const attribute = this.product.attributes.find(a => a.productAttributeId === productAttributeId);
              attribute.options.forEach((option) => {
                option.selected = false;
              });
            });
            productAttributeIds.forEach((productAttributeId) => {
              const attribute = this.product.attributes.find(a => a.productAttributeId === productAttributeId);
              let attributeSet = false;
              attribute.options.forEach((option) => {
                const formOptions: SelectAttribute[] = attributeForm.controls.options.value;
                const productOption = formOptions.find(o => o.productId === option.associatedProductId);
                if (!attributeSet && productOption && productOption.noSelected > 0 && !option.selected) {
                  option.selected = true;
                  attributeSet = true;
                  productOption.noSelected = productOption.noSelected - 1;
                }
              });
            });
          }
        } else {
          const attribute = this.product.attributes.find(a => a.productAttributeId === attributeForm.controls.productAttributeId.value);
          attribute.options.forEach((option) => {
            option.selected = false;
          });
          attributesValid = attributesValid && attributeForm.valid;
          if (attributeForm.controls.selectedOptions?.value) {
            attributeForm.controls.selectedOptions.value.forEach((selectedValue: SelectAttribute) => {
              const selected = attribute.options.find(o => o.id === selectedValue.optionId);
              if (selected) {
                selected.selected = true
              }
            });
          } else if (attributeForm.controls.selectedOption?.value) {
            const selectedValue = attributeForm.controls.selectedOption.value as SelectAttribute;
            const selected = attribute.options.find(o => o.id === selectedValue.optionId);
            if (selected) {
              selected.selected = true
            }
          }
        }
      });
      if (attributesValid) {
        this.product.listName = "Product Detail";
        this.product.listId = this.listId;
        this.cartService.addToCartWithAttributes(this.product, this.cartForm.controls.quantity.value, this.listId);
      } else {
        const message = 'Please ensure you have chosen the options correctly';
        this.snackBar.open(message, 'X', { panelClass: ['error'], verticalPosition: 'top', duration: 3000 });
      }
    } else {
      this.product.listName = "Product Detail";
      this.product.listId = this.listId;
      this.cartService.addToCartDetail(this.product, this.cartForm.controls.quantity.value);
    }
  }

  public backSoon(): void {
    const message = 'Sorry, this product will be available again soon';
    this.snackBar.open(message, 'X', { panelClass: ['error'], verticalPosition: 'top', duration: 3000 });
  }

  public adjustPrice(): void {
    if (this.attributeForms && this.attributeForms.length > 0) {
      let addedPrice = 0;
      this.attributeForms.forEach((attributeForm) => {
        const attribute = this.product.attributes.find(a => a.productAttributeId === attributeForm.controls.productAttributeId.value);
        if (attributeForm.controls.selectedOptions?.value) {
          attributeForm.controls.selectedOptions.value.forEach((selectedValue: SelectAttribute) => {
            addedPrice += attribute.options.find(o => o.id === selectedValue.optionId)?.priceAdjustment;
          });
        } else if (attributeForm.controls.selectedOption?.value) {
          const selectedValue = attributeForm.controls.selectedOption.value as SelectAttribute;
          addedPrice += attribute.options.find(o => o.id === selectedValue.optionId)?.priceAdjustment;
        }
      });
      if (addedPrice) {
        this.product.adjustedPrice = this.product.newPrice + addedPrice;
        this.product.adjustedPriceDisplay = this.currencyService.formatCurrency(this.product.currency, this.product.adjustedPrice);
        this.product.adjustedInstallmentPrice = this.currencyService.installmentPrice(this.product.adjustedPrice);
        this.product.adjustedInstallmentPriceDisplay = this.currencyService.formatCurrency(this.product.currency, this.product.adjustedInstallmentPrice);
      } else {
        this.product.adjustedPrice = 0;
        this.product.adjustedPriceDisplay = '';
        this.product.adjustedInstallmentPrice = 0;
        this.product.adjustedInstallmentPriceDisplay = '';
      }
    } else {
      this.product.adjustedPrice = 0;
      this.product.adjustedPriceDisplay = '';
      this.product.adjustedInstallmentPrice = 0;
      this.product.adjustedInstallmentPriceDisplay = '';
    }
  }

  public attChangeDropDown(event: MatSelectChange, attributeForm: FormGroup): void {
    const selectedOption = event.value as SelectAttribute;
    attributeForm.patchValue({ selectedOption });
    if (this.product?.attributes?.length === 1 && selectedOption.productId) {
      //this.parent.swapProduct(selectedOption.productId);
    }
    this.adjustPrice();
  }

  public attChangeRadio(event: MatRadioChange, attributeForm: FormGroup): void {
    const selectedOption = event.value as SelectAttribute;
    attributeForm.patchValue({ selectedOption });
    if (this.product?.attributes?.length === 1 && selectedOption.productId) {
      //this.parent.swapProduct(selectedOption.productId);
    }
    this.adjustPrice();
  }

  public attChangeCheckboxes(event: MatCheckboxChange, selectedOption: SelectAttribute, attributeForm: FormGroup): void {
    let selectedOptions = attributeForm.controls.selectedOptions.value as SelectAttribute[];
    if (event.checked) {
      if (selectedOptions) {
        selectedOptions.push(selectedOption);
      } else {
        selectedOptions = [selectedOption];
      }
      attributeForm.patchValue({ selectedOptions });
    } else {
      if (selectedOptions) {
        selectedOptions = selectedOptions.filter(o => o.optionId !== selectedOption.optionId);
      } else {
        selectedOptions = null;
      }
      attributeForm.patchValue({ selectedOptions });
    }
    this.adjustPrice();
  }

  public multiOptionClick(attributeForm: FormGroup, optionId: number, count: number): void {
    // See if we are over the number
    var noSelected = 0;
    let options = attributeForm.controls.options.value as SelectAttribute[];
    options.forEach((option) => {
      if (option.optionId !== optionId) {
        noSelected = noSelected + option.noSelected;
      }
    });
    const option = options.find(o => o.optionId === optionId);
    if (!option) {
      // Something wrong here
      attributeForm.patchValue({ selected: 0 });
      return;
    }
    if (noSelected + count > attributeForm.controls.maximum.value) {
      const message = `${attributeForm.controls.textPrompt.value} - please select a maximum of ${attributeForm.controls.maximum.value}`;
      this.snackBar.open(message, 'X', { panelClass: ['error'], verticalPosition: 'top', duration: 3000 });
    } else {
      option.noSelected = count;
      attributeForm.patchValue({ noSelected: noSelected + count });
      attributeForm.patchValue({ options });
    }
  }

  // ---------------------------------------------------------------
  // Back in Stock Sub - Logon and registration
  // ---------------------------------------------------------------
  public onRegisterFormSubmit(): void {
    if (this.registerForm.valid) {
      this.subscriptions.push(this.recaptchaV3Service.execute('Register').subscribe(
        token => {
          this.recentToken = token;
          const regRequest = new RegRequest();
          regRequest.token = token;
          regRequest.email = this.registerForm.controls.email.value;
          regRequest.customer = new Customer();
          regRequest.customer.firstName = this.registerForm.controls.firstName.value;
          regRequest.customer.lastName = this.registerForm.controls.lastName.value;
          regRequest.password = this.registerForm.controls.password.value;
          regRequest.newsLetter = this.registerForm.controls.newsLetter.value;
          regRequest.isApproved = true;
          this.subscriptions.push(this.accountService.register(regRequest).subscribe(
            regRes => {
              const result = regRes as RegResult;
              if (result.success && result.jwtToken && result.customer) {
                this.backInStock();
              } else {
                let errMessage = 'Registration failed';
                if (result.errors?.length > 0) {
                  for (const error of result.errors) {
                    errMessage += ` ... ${error}`;
                  }
                }
                this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
              }
            }));
        }));
    } else {
      const errMessage = 'There is something wrong with the registration values, please review them';
      this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
    }
  }

  public onLoginFormSubmit(): void {
    if (this.loginForm.valid) {
      this.subscriptions.push(this.recaptchaV3Service.execute('Logon').subscribe(
        token => {
          this.recentToken = token;
          const request = new LogonRequest();
          request.token = token;
          request.email = this.loginForm.controls.email.value;
          request.password = this.loginForm.controls.password.value;
          request.checkout = false;
          this.subscriptions.push(this.accountService.logon(request).subscribe(
            logonRes => {
              const result = logonRes as LogonResult;
              if (result.success === true && result.jwtToken) {
                this.backInStock();
              } else {
                let errMessage = 'Logon failed';
                if (result.errors?.length > 0) {
                  for (const error of result.errors) {
                    errMessage += ` ... ${error}`;
                  }
                }
                this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
              }
            }));
        }));
    } else {
      const errMessage = 'There is something wrong with the logon values, please review them';
      this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
    }
  }

  public resetPassword(): void {
    if (this.loginForm.controls.email.valid) {
      this.subscriptions.push(this.recaptchaV3Service.execute('ResetPasswordReq').subscribe(
        token => {
          this.recentToken = token;
          const request = new LogonRequest();
          request.token = token;
          request.email = this.loginForm.controls.email.value;
          request.password = '';
          request.checkout = false;
          this.subscriptions.push(this.accountService.reset(request).subscribe(
            resetRes => {
              const result = resetRes as LogonResult;
              if (result.success) {
                const infoMessage = 'Please check your email to get your reset link.';
                this.snackBar.open(infoMessage, 'X', { panelClass: 'success', verticalPosition: 'top', duration: 6000 });
              } else {
                const errMessage = 'Could not find a user with that email. If you had an account on the previous site we will be migrating them soon.';
                this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 6000 });
              }
            }));
        }));
    } else {
      const errMessage = 'Enter your account email address then click the button.';
      this.snackBar.open(errMessage, 'X', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
    }
  }

  public backInStock(): void {
    this.subscriptions.push(this.customerService.backInStock(this.customerService.getCustomerGuid(), this.product).subscribe(
      res => {
        this.informed = res as boolean;
        if (res) {
          this.toastr.success('You will receive an email when we have more stock.', 'Stock Subscribe');
        } else {
          this.toastr.warning('Sorry, something went wrong with the out of stock subscription.', 'Stock Subscribe');
        }
      }));
  }

  public isLoggedIn(): void {
    this.loginForm.patchValue({ email: this.customer?.email });
    this.registerForm.patchValue({ email: this.customer?.email });
    this.registerForm.patchValue({ firstName: this.customer?.firstName });
    this.registerForm.patchValue({ lastName: this.customer?.lastName });
    this.loggedIn = false;
    if (localStorage.getItem('token')) {
      this.subscriptions.push(this.accountService.refresh(localStorage.getItem('token'), this.customerService.getCustomerGuid()).subscribe(
        resRefresh => {
          const result = resRefresh as RefreshResponse;
          if (result?.jwtToken) {
            localStorage.setItem('token', result.jwtToken);
            this.claimsService.set(result.claims);
            this.loggedIn = true;
          }
          if (result?.isGuest) {
            this.stockRegister = true; // Register
          } else {
            this.stockRegister = false; // Logon
          }
        }));
    } else if (this.customerService.getCustomerGuid()) {
      this.subscriptions.push(this.accountService.isGuest(this.customerService.getCustomerGuid()).subscribe(
        resGuest => {
          const isGuest = resGuest as boolean;
          if (isGuest) {
            this.stockRegister = true; // Register
          } else {
            this.stockRegister = false; // Logon
          }
        }));
    } else {
      this.stockRegister = true; // Register
    }
  }
  counter(i: number) {
    return new Array(i);
  }
}
